<template>
  <div class="container">
    <div v-if="campaignWebId">
      <p>
        Send SMS with a dynamic text to a series of recipients. Click 'Create' to get started.

        <sdx-expand-and-collapse arrow-position="dynamic" expand-label="Show more" collapse-label="Show less">
          <br/>
          <p>
            A campaign consists of all necessary information to send a range of SMS messages to a defined list of
            recipients.
            The recipient phone numbers need to be prepared and stored in a CSV file.
            Additional text elements or the entire message content can be defined alongside the phone numbers.
            The file containing all this information needs to be ready beforehand and is uploaded during the campaign
            creation.
          </p>
          <p>
            After creating a campaign, it can be reviewed and sent.
            Depending on the number of messages, the sending can take some time.
            An overview page shows the progress as well as previous campaigns and their details.
          </p>
        </sdx-expand-and-collapse>
      </p>
    </div>
    <div id="title-of-save">
      <h1 id="title-text-child">
        Campaigns
      </h1>
    </div>
    <div class="row margin-bottom-2">
      <div class="col-xs-4">
        <DevAppDropDownComponent role="campaign_manager" @devapp-changed="devAppChanged"/>
      </div>
      <div class="col-lg text-align-right margin-top-3" v-if="campaignWebId">
        <sdx-button
            id="createButton"
            data-testid="createButton"
            name="CreateCampaign"
            label="Create"
            class="sdx-button-object"
            @click="this.$router.push('/campaigns/create');"
        />
      </div>
    </div>
    <sdx-loading-spinner id="spinner-campaigns" data-testId="spinner-campaigns" size="large"
                         sr-hint="Loading. Please wait."
                         class="spinner-dashboard"/>
    <div id="campaigns" class="row margin-bottom-1"
         v-if="campaignWebId && Array.isArray(campaignsList) && campaignsList.some(campaignItem => campaignItem.status === 'CREATED')">
      <div class="col">
        <sdx-accordion id="accordionCreated">
          <sdx-accordion-item open>
            <sdx-accordion-item-header>
              <span class="h4">Drafts</span>
            </sdx-accordion-item-header>
            <sdx-accordion-item-body>
              <div class="campaign-home-table table table--responsive table--highlight" :key="table"
                   data-testid="tableDrafts">
                <div calss="table_wrapper">
                  <table data-init="auto">
                    <thead>
                    <tr>
                      <th class="table-header col-xs-1">Date</th>
                      <th class="table-header col-xs-4">Name</th>
                      <th class="table-header text-align-center col-xs-2">Recipients</th>
                      <th class="table-header text-align-center col-xs-2">Actions</th>
                    </tr>
                    </thead>
                    <tbody data-testid="tableDrafts-body">
                    <tr v-for="(campaignItem) in this.campaignsList.filter(item => item.status === 'CREATED')"
                        :key="campaignItem.key" :data-testid="'campaign_' + campaignItem.id">
                      <td>{{ formatDate(campaignItem.statusUpdateTime) }}</td>
                      <td>{{ campaignItem.name }}</td>
                      <td class="text-align-center">{{ formatBigNumber(campaignItem.totalMessages) }}</td>
                      <td>
                        <div class="toolbar text-align-center" id="toolbar01">
                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Send"
                                    @click="saveIdAndGoToRoute(campaignItem, '/campaigns/summary#SendCampaign');"
                                    data-testid="send">
                              <i class="icon icon-send" aria-hidden="true" title="Send"></i>
                            </button>
                          </div>

                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Summary"
                                    @click="saveIdAndGoToRoute(campaignItem, '/campaigns/summary');"
                                    data-testid="summaryDrafts">
                              <i class="icon icon-eye" aria-hidden="true" title="Summary"></i>
                            </button>
                          </div>

                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Edit Recipients"
                                    id="editRecipientsActionButton"
                                    data-testid="editRecipientsActionButton"
                                    @click="saveIdAndGoToRoute(campaignItem,'/campaigns/create?campaignId='+campaignItem.id)"
                                    :disabled="campaignItem.status != 'CREATED'">
                              <i class="icon icon-upload" aria-hidden="true" title="Edit Recipients"></i>
                            </button>
                          </div>

                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Edit Text"
                                    id="editTextActionButton"
                                    data-testid="editTextActionButton"
                                    @click="saveIdAndGoToRoute(campaignItem, '/campaigns/edit');"
                                    :disabled="campaignItem.status != 'CREATED'">
                              <i class="icon icon-edit" aria-hidden="true" title="Edit Text"></i>
                            </button>
                          </div>

                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Copy"
                                    :data-testid="'copyDrafts_' + campaignItem.id"
                                    @click="cloneCampaign(campaignItem)">
                              <i class="icon icon-copy" aria-hidden="true" title="Clone"></i>
                            </button>
                          </div>

                          <sdx-dialog
                              id="deleteDialog"
                              data-testid="deleteDialog"
                              label="Would you like to delete this file?"
                              icon-name="icon-bin"
                              ref="deleteDialog"
                              type="closable-modal"
                          >
                            <sdx-dialog-toggle>
                              <div class="toolbar__item item--show padding-icon">
                                <button aria-label="Bin"
                                        :data-testid="'binDrafts_' + campaignItem.id">
                                  <i class="icon icon-bin" aria-hidden="true" title="Delete"></i>
                                  <span class="toolbar__label"></span>
                                </button>
                              </div>
                            </sdx-dialog-toggle>
                            <sdx-dialog-content>
                              <p>Do you really want to delete <strong>{{ campaignItem.name }}</strong>?</p>
                              <sdx-button-group>
                                <sdx-button id="noDeleteButton" :data-testid="'noDraftsDeleteButton_' + campaignItem.id"
                                            label="No"
                                            @click="closeDeleteDialog()"
                                            theme="secondary"></sdx-button>
                                <sdx-button id="yesDeleteButton"
                                            :data-testid="'yesDraftsDeleteButton_' + campaignItem.id" label="Yes"
                                            @click="deleteCampaign(campaignItem)"
                                ></sdx-button>
                              </sdx-button-group>
                            </sdx-dialog-content>
                          </sdx-dialog>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </sdx-accordion-item-body>
          </sdx-accordion-item>
        </sdx-accordion>
      </div>
    </div>
    <div class="row margin-bottom-1"
         v-if="campaignWebId && Array.isArray(campaignsList) && campaignsList.some(campaignItem => campaignItem.status === 'PROGRESS')">
      <div class="col">
        <sdx-accordion id="accordionProgress" class="bg-horizon">
          <sdx-accordion-item open>
            <sdx-accordion-item-header>
              <span class="h4">In Progress</span>
            </sdx-accordion-item-header>
            <sdx-accordion-item-body>
              <div class="campaign-home-table table table--responsive table--highlight" data-testid="table-inProgress">
                <table>
                  <thead>
                  <tr>
                    <th class="table-header col-xs-1">Date</th>
                    <th class="table-header col-xs-2">Name</th>
                    <th class="table-header text-align-center col-xs-1">Recipients</th>
                    <th class="table-header text-align-center col-xs-1">Progress</th>
                    <th class="table-header text-align-center col-xs-1">Successful</th>
                    <th class="table-header text-align-center col-xs-1">Failed</th>
                    <th class="table-header text-align-center col-xs-1">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="campaignItem in this.campaignsList.filter(item => item.status === 'PROGRESS')"
                      :key="campaignItem.id">
                    <td>{{ formatDate(campaignItem.statusUpdateTime) }}</td>
                    <td>{{ campaignItem.name }}</td>
                    <td class="text-align-center">{{ formatBigNumber(campaignItem.totalMessages) }}</td>
                    <td class="text-align-center">
                      <div
                          v-if="campaignItem.totalMessages>0"
                          id="bar02" class="loader-bar">
                        <div class="detail loader-bar-minimal">
                          <div class="detail-right">
                            <div class="progress">
                                <span>
                                  {{ calcPercent(campaignItem) }} %
                                </span>
                            </div>
                          </div>
                        </div>
                        <div class="loader-bar--progress">
                          <div class="indicator" :style="{ width: calcPercent(campaignItem) + '%' }"></div>
                        </div>
                      </div>
                    </td>
                    <td class="text-align-center">
                        <span v-if="campaignItem.successfulMessages>0" style="color: green">
                          {{ formatBigNumber(campaignItem.successfulMessages) }}
                        </span>
                      <span v-if="campaignItem.successfulMessages==0">
                          -
                        </span>
                    </td>
                    <td class="text-align-center">
                        <span v-if="campaignItem.failedMessages>0"
                              style="color: red">
                          {{ formatBigNumber(campaignItem.failedMessages) }}
                        </span>
                      <span v-if="campaignItem.failedMessages==0">
                          -
                        </span>
                    </td>
                    <td>

                      <div class="toolbar text-align-center" id="toolbar01">

                        <div class="toolbar__item item--show padding-icon">
                          <button aria-label="Summary"
                                  @click="saveIdAndGoToRoute(campaignItem, '/campaigns/summary');">
                            <i class="icon icon-eye" aria-hidden="true" title="Summary"></i>
                          </button>
                        </div>
                        <div class="toolbar__item item--show padding-icon">
                          <button aria-label="Summary"
                                  @click="saveIdAndGoToOutBox(campaignItem);">
                            <i class="icon icon-link" aria-hidden="true" title="Check delivery logs (Outbox)"></i>
                          </button>
                        </div>

                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </sdx-accordion-item-body>
          </sdx-accordion-item>
        </sdx-accordion>
      </div>
    </div>
    <div class="row margin-bottom-1"
         v-if="campaignWebId && Array.isArray(campaignsList) && campaignsList.some(campaignItem => campaignItem.status === 'FINISHED')">
      <div class="col">
        <sdx-accordion id="accordionFinished">
          <sdx-accordion-item open>
            <sdx-accordion-item-header>
              <span class="h4">Finished</span>
            </sdx-accordion-item-header>
            <sdx-accordion-item-body>
              <div class="campaign-home-table table table--responsive table--highlight" data-testid="tableFinished">
                <table>
                  <thead>
                  <tr>
                    <th class="table-header col-xs-1">Date</th>
                    <th class="table-header col-xs-4">Name</th>
                    <th class="table-header text-align-center col-xs-1">Recipients</th>
                    <th class="table-header text-align-center col-xs-1">Successful</th>
                    <th class="table-header text-align-center col-xs-1">Failed</th>
                    <th class="table-header text-align-center col-xs-1">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="campaignItem in this.campaignsList.filter(item => item.status === 'FINISHED')"
                      :key="campaignItem.id" :data-testid="'campaign_' + campaignItem.id">
                    <td>{{ formatDate(campaignItem.statusUpdateTime) }}</td>
                    <td>{{ campaignItem.name }}</td>
                    <td class="text-align-center">{{ formatBigNumber(campaignItem.totalMessages) }}</td>
                    <td class="text-align-center">
                        <span v-if="campaignItem.successfulMessages>0" style="color: green">
                          {{ formatBigNumber(campaignItem.successfulMessages) }}
                        </span>
                      <span v-if="campaignItem.successfulMessages==0">
                          -
                        </span>
                    </td>
                    <td class="text-align-center">
                        <span v-if="campaignItem.failedMessages>0"
                              style="color: red">
                          {{ formatBigNumber(campaignItem.failedMessages) }}
                        </span>
                      <span v-if="campaignItem.failedMessages==0">
                          -
                        </span>
                    </td>
                    <td>
                      <div class="toolbar text-align-center" id="toolbar01">
                        <div class="toolbar__item item--show padding-icon">
                          <button aria-label="Summary"
                                  data-testid="summaryF"
                                  @click="saveIdAndGoToRoute(campaignItem, '/campaigns/summary');">
                            <i class="icon icon-eye" aria-hidden="true" title="Summary"></i>
                          </button>
                        </div>

                        <div class="toolbar__item item--show padding-icon">
                          <button aria-label="Copy"
                                  @click="cloneCampaign(campaignItem)"
                                  data-testid="copyF">
                            <i class="icon icon-copy" aria-hidden="true" title="Clone"></i>
                          </button>
                        </div>

                        <div class="toolbar__item item--show padding-icon">
                          <button aria-label="OutBox"
                                  data-testid="outbox"
                                  @click="saveIdAndGoToOutBox(campaignItem);">
                            <i class="icon icon-link" aria-hidden="true" title="Check delivery logs (Outbox)"></i>
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </sdx-accordion-item-body>
          </sdx-accordion-item>
        </sdx-accordion>
      </div>
    </div>
    <div class="row"
         v-if="campaignWebId && Array.isArray(campaignsList) && campaignsList.some(campaignItem => (campaignItem.status != 'CREATED' && campaignItem.status != 'PROGRESS' && campaignItem.status != 'FINISHED'))">
      <div class="col">
        <sdx-accordion id="accordionWaiting" class="bg-horizon">
          <sdx-accordion-item open>
            <sdx-accordion-item-header>
              <span class="h4">Waiting...</span>
            </sdx-accordion-item-header>
            <sdx-accordion-item-body>
              <div class="campaign-home-table table table--responsive table--highlight" data-testid="tableWaiting">
                <table>
                  <thead>
                  <tr>
                    <th class="table-header col-xs-1">Date</th>
                    <th class="table-header col-xs-4">Name</th>
                    <th class="table-header text-align-center col-xs-1">~Recipients</th>
                    <th class="table-header text-align-center col-xs-1">~Successful</th>
                    <th class="table-header text-align-center col-xs-1">~Failed</th>
                    <th class="table-header text-align-center col-xs-1">Status</th>
                    <th class="table-header text-align-center col-xs-1">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="campaignItem in campaignsList" :key="campaignItem.id">
                    <template
                        v-if="campaignItem.status != 'CREATED' && campaignItem.status != 'PROGRESS' && campaignItem.status != 'FINISHED'">
                      <td>{{ formatDate(campaignItem.statusUpdateTime) }}</td>
                      <td>{{ campaignItem.name }}</td>
                      <td class="text-align-center">{{ formatBigNumber(campaignItem.totalMessages) }}</td>
                      <td class="text-align-center">
                        <span v-if="campaignItem.successfulMessages>0" style="color: green">
                          {{ formatBigNumber(campaignItem.successfulMessages) }}
                        </span>
                        <span v-if="campaignItem.successfulMessages==0">
                          -
                        </span>
                      </td>
                      <td class="text-align-center">
                        <span v-if="campaignItem.failedMessages>0"
                              style="color: red">
                          {{ formatBigNumber(campaignItem.failedMessages) }}
                        </span>
                        <span v-if="campaignItem.failedMessages==0">
                          -
                        </span>
                      </td>
                      <td class="text-align-center">{{ campaignItem.status }}</td>
                      <td>
                        <div class="toolbar text-align-center" id="toolbar01">
                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Summary"
                                    @click="saveIdAndGoToRoute(campaignItem, '/campaigns/summary');">
                              <i class="icon icon-eye" aria-hidden="true" title="Summary"></i>
                            </button>
                          </div>

                          <div class="toolbar__item item--show padding-icon">
                            <button aria-label="Summary"
                                    @click="saveIdAndGoToOutBox(campaignItem);">
                              <i class="icon icon-link" aria-hidden="true" title="Check delivery logs (Outbox)"></i>
                            </button>
                          </div>
                        </div>
                      </td>
                    </template>
                  </tr>
                  </tbody>
                </table>
              </div>
            </sdx-accordion-item-body>
          </sdx-accordion-item>
        </sdx-accordion>
      </div>
    </div>
  </div>
</template>

<script>
import loadingSpinnerService from '@/services/loading-spinner.service';
import DevAppDropDownComponent from "@/components/DevAppDropDownComponent";
import campaignsService from "@/modules/campaigns/service";
import {logger} from "@/services/logger.service";
import {formatBigNumber, formatDate} from "@/util/utility";
import numeral from "numeral";
import {showAlert, showSuccess} from "@/services/notification.service";
import envService from "@/services/env.service";

export default {
  components: {
    DevAppDropDownComponent,
  },
  data: function () {
    return {
      campaignWebId: undefined,
      campaignsList: undefined,
      response: undefined,
      table: 0,
    };
  },
  async mounted() {
    await this.loadCampaignList();
    if (campaignsService.getCampaignsListFromSession().some(campaignItem => (campaignItem.status === 'PROGRESS' || campaignItem.status === 'BUILDINGTEXT')))
      setTimeout(this.reloadCampaign, 10000);
    else
      logger.debug("No campaign in progress -> No reloading! ")
  },
  methods: {
    formatBigNumber,
    formatDate,
    calcPercent(campaignItem) {
      const value = (parseInt(campaignItem.failedMessages) + parseInt(campaignItem.successfulMessages)) / parseInt(campaignItem.totalMessages) * 100
      return numeral(value).format("0")
    },
    devAppChanged: function (webId) {
      this.campaignWebId = webId;
      campaignsService.setCampaignWebId(webId);
      this.campaignsList = undefined;
      this.loadCampaignList();
    },
    async reloadCampaign() {
      logger.debug("Reload Campaigns")
      var redo = false;
      if (this.campaignsList !== undefined && this.campaignsList !== null) {
        for (var i = 0; i < this.campaignsList.length; i++) {
          if (this.campaignsList[i].status === 'PROGRESS' || this.campaignsList[i].status === 'BUILDINGTEXT') {
            const response = await campaignsService.getCampaign(campaignsService.getCampaignWebId(), this.campaignsList[i].id)
            const campaign = response.data;
            this.campaignsList[i] = campaign;
            if (campaign.status === 'PROGRESS' || campaign.status === 'BUILDINGTEXT')
              redo = true;
          }
        }
        if (redo && this.$route.path === '/campaigns') {
          setTimeout(this.reloadCampaign, 10000);
        } else {
          this.campaignsList = undefined;
          this.loadCampaignList();
        }
      }
    },
    async loadCampaignList() {
      loadingSpinnerService.show("campaigns")

      try {
        if (campaignsService.getCampaignWebId() !== undefined && campaignsService.getCampaignWebId() !== null) {
          campaignsService.removeCampaignsList();
          if (this.campaignsList === undefined) {
            this.campaignsList = campaignsService.getCampaignsListFromSession()
            logger.debug("Loaded from Cache: " + JSON.stringify(this.campaignsList))
            if (this.campaignsList === undefined || this.campaignsList === null) {
              logger.debug("Load from API")
              logger.debug("CampaignWebId:" + campaignsService.getCampaignWebId())
              const response = await campaignsService.getCampaignsList(campaignsService.getCampaignWebId())
              this.campaignsList = response.data;
              campaignsService.setCampaignsListToSession(this.campaignsList)
            }
          }
        }
      } catch (error) {
        showAlert(error.message)
      }
      loadingSpinnerService.hide("campaigns")
    },
    saveIdAndGoToRoute(campaign, route) {
      try {
        campaignsService.setCampaignToSession(campaign)
        this.$router.push(route);
      } catch (error) {
        showAlert(error.message)
      }
    },
    saveIdAndGoToOutBox(campaign) {
      campaignsService.setCampaignToSession(campaign)
      if (envService.featureToggleSmsTag()) {
        this.$router.push({name: 'outbox', query: {tag: campaign.id.substr(0, 8), webId: this.campaignWebId}});
      } else {
        this.$router.push({name: 'outbox', query: {webId: this.campaignWebId}});
      }
    },
    async deleteCampaign(campaign) {
      try {
        await campaignsService.deleteCampaign(campaignsService.getCampaignWebId(), campaign.id);
        logger.debug("Delete Campaign=" + JSON.stringify(campaign));
        showSuccess("Deleted Campaign: '" + campaign.name + "'")
        this.reloadCampaign();
      } catch (Exception) {
        logger.error("Could not delete Campaign!")
        showAlert("Could not delete Campaign!")
      }
      document.getElementById('deleteDialog').close()
      await this.closeDeleteDialog()
    },
    async closeDeleteDialog() {
      await this.$refs.deleteDialog.close()
    },
    async cloneCampaign(campaign) {
      logger.debug("Start clone...")
      try {
        let newCampaignId = await campaignsService.cloneCampaign(campaignsService.getCampaignWebId(), campaign.id);
        logger.debug("Cloned Campaign: " + campaign.id + " to " + newCampaignId);
        showSuccess("Cloned Campaign '" + campaign.name + "'")
        await this.reloadCampaign();
      } catch (Exception) {
        logger.error("Could not clone Campaign!")
        showAlert("Could not clone Campaign!")
      }
      logger.debug("End clone...")
    }

  }
}
</script>

<style scoped>

.campaign-home-table.table tbody td {
  padding-top: 5px;
  padding-bottom: 3px;
}

.padding-icon {
  padding: 4px 4px 4px 4px;
}

.loader-bar-minimal {
  margin-bottom: 0px !important;
}
</style>
